import React from "react";
import { useDelete } from "../../../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../../../../i18n";
import ModalForm from "@ui/Modal/Form";
import { useSetups, type Setup } from "../../../../../../api/useSetups";
import type { Project } from "../../../../../../api/useProjects";
import Text from "@ui/Text";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { useImpactedAnalyses } from "../../useAnalysisImpacted";
import useActiveAnalysesStream from "../../../../../../api/useActiveAnalysesStream";

interface P {
  onRequestClose: () => void;
  project: Project;
  setup: Setup;
}

const analysisStatus = new Set(["DIAGNOSIS", "DIAGNOSIS_ERROR"] as const);

export const DeleteFailuresModalForm = ({ project, setup, onRequestClose }: P) => {
  const { addToast } = useToasts();
  const setupId = setup.id;
  const projectId = project.id;
  const deleteFailures = useDelete(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/diagnosis/failures",
  );
  const { activeAnalyses } = useActiveAnalysesStream(projectId);

  const impactedAnalyses = useImpactedAnalyses({
    setups: useSetups(projectId),
    activeAnalyses,
    setupId,
    analysisStatus,
  });
  const showAdditionalMessage = setup.diagnosisStatus === "COMPLETE" && impactedAnalyses.length > 0;

  const handleSubmit = React.useCallback(async () => {
    await deleteFailures({ routeParams: { projectId, setupId } });
    addToast("success", t("setup.diagnosis.failures.delete_failures.toast.success"));
    return true;
  }, [addToast, deleteFailures, projectId, setupId]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={handleSubmit}
      okButtonName={t("modal.delete")}
      okButtonVariant="destructive"
    >
      <Text>{t("setup.paragraphs.failures.modal.delete_failures.body")}</Text>
      {showAdditionalMessage ? (
        <AdditionnalMessage
          status="WARNING"
          text={t("setup.modal.delete.analysis.additionnal_message.warning", {
            diagnosisValid: setup.diagnosisStatus === "COMPLETE",
            impactedAnalysisCount: impactedAnalyses.length,
          })}
        />
      ) : null}
    </ModalForm>
  );
};
